import isMobile from 'ismobilejs';
import moment from 'moment';

export const getBaseUrl = () => {
  const { APP_TOKEN_PROTOCOL, APP_TOKEN_HOST, APP_TOKEN_PORT, location } = window;

  if (!APP_TOKEN_HOST) {
    return '';
  }

  const protocol = APP_TOKEN_PROTOCOL || location.protocol.replace(':', '');
  const port = APP_TOKEN_PORT ? `:${APP_TOKEN_PORT}` : '';
  const baseUrl = `${protocol}://${APP_TOKEN_HOST}${port}`;

  return baseUrl;
};

export const redirectTo = (url, newTab = false) => {
  newTab ? window.open(url) : (window.location.href = url);
};

export const getUrlQueryParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlParam = urlParams.get(param);

  return urlParam;
};

/**
 * Functional/auth query params consumed by the app itself. They are excluded
 * from referral capture so we never leak auth tokens (`code`, `ott`) into
 * analytics, nor store internal routing flags (`flow`) as referral data.
 */
const NON_REFERRAL_QUERY_PARAMS = ['code', 'ott', 'flow'];

/**
 * getAllUrlQueryParams
 *
 * @description Returns every query param present in the current URL as a plain
 * object, partner-agnostic — no predefined list, nothing dropped. Values are
 * captured exactly as sent. Returns an empty object when there are no params.
 * @returns {Object<string, string>}
 */
export const getAllUrlQueryParams = () => {
  const urlParams = new URLSearchParams(window.location.search);

  return Object.fromEntries(urlParams.entries());
};

/**
 * getReferralQueryParams
 *
 * @description All URL query params except the app's own functional/auth params
 * (see NON_REFERRAL_QUERY_PARAMS), used for referral/marketing attribution.
 * @returns {Object<string, string>} empty object when no referral params exist
 */
export const getReferralQueryParams = () => {
  const allParams = getAllUrlQueryParams();

  return Object.keys(allParams).reduce((acc, key) => {
    if (!NON_REFERRAL_QUERY_PARAMS.includes(key)) {
      acc[key] = allParams[key];
    }

    return acc;
  }, {});
};

const REFERRAL_PARAMS_STORAGE_KEY = 'referralParams';

const hasKeys = (obj) => !!obj && Object.keys(obj).length > 0;

/**
 * captureReferralParams
 *
 * @description Reads referral params from the current URL and, when present,
 * persists them in sessionStorage for the rest of the onboarding session. This
 * is called once at the onboarding entry point so the params survive later steps
 * where the URL no longer carries them (the case may be created on a later step).
 * @returns {Object<string, string>} the referral params captured from the URL
 */
export const captureReferralParams = () => {
  const referralParams = getReferralQueryParams();

  if (hasKeys(referralParams)) {
    try {
      sessionStorage.setItem(REFERRAL_PARAMS_STORAGE_KEY, JSON.stringify(referralParams));
    } catch {
      // sessionStorage may be unavailable (private mode / quota) — non-fatal.
    }
  }

  return referralParams;
};

/**
 * getStoredReferralParams
 *
 * @description Returns the referral params captured for this session, falling
 * back to the current URL when sessionStorage is empty or unavailable.
 * @returns {Object<string, string>} empty object when no referral params exist
 */
export const getStoredReferralParams = () => {
  try {
    const stored = sessionStorage.getItem(REFERRAL_PARAMS_STORAGE_KEY);

    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Fall through to the URL-based params below.
  }

  return getReferralQueryParams();
};

/**
 * applyReferralParamsToCasePayload
 *
 * @description Injects the captured referral params into a create-case payload's
 * `custom.referralParams`, so the backend persists them with the case. Used as
 * the `overrideCreateCasePayload` hook on case creation. No-op when there are no
 * referral params, so cases without referral data are unaffected.
 * @param {Object} caseData the create-case payload
 * @returns {Object} the (possibly enriched) payload
 */
export const applyReferralParamsToCasePayload = (caseData) => {
  const referralParams = getStoredReferralParams();

  if (hasKeys(referralParams)) {
    caseData.custom = { ...(caseData.custom || {}), referralParams };
  }

  return caseData;
};

/**
 * validateDateByRules
 *
 * @description Takes a `rulesArr` and interates over the rules to check if the `momentDate` matches the rules
 * @param {moment} momentDate Moment object
 * @param {Object[]} rulesArr Array of objects, where each object has `operator` (moment function name) and `value` (string representing date)
 * @returns Boolean
 */
export const validateDateByRules = (momentDate, rulesArr) => {
  for (let i = 0; i < rulesArr.length; i += 1) {
    const rule = rulesArr[i];

    if (typeof momentDate[rule.operator] === 'function') {
      const isValid = momentDate[rule.operator](rule.value);

      if (!isValid) return false;
    }
  }

  return true;
};

export const getPfdObjectUrlFromArrayBuffer = (arrayBuffer) => {
  const file = new Blob([arrayBuffer], { type: 'application/pdf' });
  return URL.createObjectURL(file);
};

export const downloadDocument = (arrayBuffer, docType, fileName = 'download', preOpenedWindow = null) => {
  if (!arrayBuffer) {
    console.warn('downloadDocument: empty arrayBuffer');
    if (preOpenedWindow) preOpenedWindow.close();
    return;
  }
  const isMobileDevice = isMobile();
  try {
    const uint8 = new Uint8Array(arrayBuffer);

    const base64 = btoa(uint8.reduce((data, byte) => data + String.fromCharCode(byte), ''));

    const isIOSBridge = !!window.webkit?.messageHandlers?.downloadDocument;
    const isRNBridge = !!window.ReactNativeWebView;
    const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);

    if (isMobileDevice.any) {
      if (isIOSBridge) {
        // iOS WKWebView
        if (preOpenedWindow) preOpenedWindow.close();
        window.webkit.messageHandlers.downloadDocument.postMessage({
          fileName,
          mimeType: docType,
          base64,
        });
        return;
      }

      if (isRNBridge) {
        // React Native WebView (Android / iOS)
        if (preOpenedWindow) preOpenedWindow.close();
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'DOWNLOAD_FILE',
            fileName,
            mimeType: docType,
            base64,
          }),
        );
        return;
      }
    }

    const blob = new Blob([arrayBuffer], { type: docType });
    const url = window.URL.createObjectURL(blob);

    if (isIOS && docType === 'application/pdf') {
      if (preOpenedWindow) {
        preOpenedWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return;
    }

    if (preOpenedWindow) preOpenedWindow.close();

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (preOpenedWindow) preOpenedWindow.close();
    console.error('downloadDocument error:', err);
  }
};

export const isScrolledToBottom = ({ target: { scrollHeight, clientHeight, scrollTop } }) => {
  const threshold = 20;
  return Math.ceil(scrollTop) >= scrollHeight - clientHeight - threshold;
};

export const getProfessionsFilteredByAge = (professions, birthDate) => {
  if (!birthDate) return professions;

  const age = moment().diff(birthDate, 'years');

  const filteredProfessions = professions.filter((item) => {
    if (item.ageRangeFrom && item.ageRangeTo) {
      return age >= item.ageRangeFrom && age <= item.ageRangeTo;
    }

    return true;
  });

  return filteredProfessions;
};
